import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Client, ClientEdit } from '../../@types/api';
import RestUtilities from '../../services/RestUtilities';
import { useSetRecoilState } from 'recoil';
import { toastState } from '../../state/RecoilState';

const sxPropsL = { my: 1, width: '100%' };
const buttonProps = {
  size: 'small' as 'small' | 'medium' | 'large',
  variant: 'contained' as 'contained' | 'outlined',
};

const EditClient: React.FC = () => {
  const { id } = useParams();
  const setToast = useSetRecoilState(toastState);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [preventReassign, setPreventReassign] = useState(false);
  const [client, setClient] = useState<ClientEdit | undefined>(undefined);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const getClient = async () => {
      const res = await RestUtilities.get(`/api/client/GetClient/${id}`);
      if (res.ok) {
        const dbClient: Client = res.content;
        const clientEdit: ClientEdit = {
          ...res.content,
          firstName: dbClient.clientNames[0]?.firstName ?? '',
          surname: dbClient.clientNames[0]?.surname ?? '',
          telephone: dbClient.clientTelephones[0]?.telephoneNumber ?? '',
          mobile: dbClient.clientTelephones[0]?.mobile ?? '',
          address1: dbClient.clientAddresses[0]?.address1 ?? '',
          address2: dbClient.clientAddresses[0]?.address2 ?? '',
          town: dbClient.clientAddresses[0]?.town ?? '',
          postCode: dbClient.clientAddresses[0]?.postCode ?? '',
        };

        setClient(clientEdit);
      } else {
        setToast('Client failed to load');
      }
    };
    if (!client) {
      getClient();
    }
  }, [client, id, setToast]);

  const changeClient = (field: keyof ClientEdit, value: string | boolean) => {
    if (client) {
      const newClient: ClientEdit = { ...client };
      newClient[field] = value as never;
      setClient(newClient);
    }
  };

  const updateClient = async () => {
    const res = await RestUtilities.post('api/client/Edit', client);
    if (res.ok) {
      setToast('Client updated');
    } else {
      setToast('Client not updated, Something went wrong');
    }
  };

  const unsubscribeClient = async () => {
    const res = await RestUtilities.get(
      `api/client/Unsubscribe?email=${client?.emailAddress}`
    );
    if (res.ok) {
      setToast('Client IsContactable is changed');
    } else {
      setToast('Client not updated, Something went wrong');
    }
    setClient(undefined);
  };

  const editEmail = () => {
    setEditEmailOpen(true);
  };

  const processEmailUpdate = async () => {
    const res = await RestUtilities.get(
      `api/client/UpdateEmail?clientId=${id}&newEmail=${newEmail}&oldEmail=${
        client?.emailAddress
      }&deleteClient=${!preventReassign}`
    );
    if (res.ok) {
      setToast(res.content);
    } else {
      setToast('Client not updated, Something went wrong');
    }
    setClient(undefined);
    setEditEmailOpen(false);
  };

  return (
    <Box>
      <Box sx={{ pt: 2 }}>
        <Typography variant='h5' color='primary'>
          Edit ClientId: {id}
        </Typography>

        {client && (
          <>
            <Typography>Email: {client.emailAddress}</Typography>
            <Grid container spacing={2} mt={2}>
              <Grid item sm={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={client.isContactable}
                      onChange={() =>
                        changeClient('isContactable', !client.isContactable)
                      }
                      disabled={true}
                    />
                  }
                  label='Is Contactable/Subscribed'
                />
              </Grid>
              <Grid item sm={12} md={6}>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='firstname'
                    label='FirstName'
                    value={client.firstName}
                    onChange={(event) => {
                      changeClient('firstName', event.target.value);
                    }}
                  />
                </FormControl>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='lastName'
                    label='LastName'
                    value={client.surname}
                    onChange={(event) => {
                      changeClient('surname', event.target.value);
                    }}
                  />
                </FormControl>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='telephone'
                    label='Telephone'
                    value={client.telephone}
                    onChange={(event) => {
                      changeClient('telephone', event.target.value);
                    }}
                  />
                </FormControl>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='mobile'
                    label='Mobile'
                    value={client.mobile}
                    onChange={(event) => {
                      changeClient('mobile', event.target.value);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item sm={12} md={6}>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='address1'
                    label='1st Line Address'
                    value={client.address1}
                    onChange={(event) => {
                      changeClient('address1', event.target.value);
                    }}
                  />
                </FormControl>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='address2'
                    label='2nd Line Address'
                    value={client.address2}
                    onChange={(event) => {
                      changeClient('address2', event.target.value);
                    }}
                  />
                </FormControl>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='town'
                    label='Town'
                    value={client.town}
                    onChange={(event) => {
                      changeClient('town', event.target.value);
                    }}
                  />
                </FormControl>
                <FormControl sx={sxPropsL}>
                  <TextField
                    size='small'
                    required
                    id='postcode'
                    label='Postcode'
                    value={client.postCode}
                    onChange={(event) => {
                      changeClient('postCode', event.target.value);
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mt={2}
            >
              <div>
                <Button
                  {...buttonProps}
                  color='warning'
                  onClick={unsubscribeClient}
                >
                  Unsubscribe Client
                </Button>
                <Button
                  {...buttonProps}
                  color='error'
                  onClick={editEmail}
                  sx={{ ml: 1 }}
                >
                  Change Email
                </Button>
              </div>
              <Button {...buttonProps} color='primary' onClick={updateClient}>
                Update Client
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Dialog open={editEmailOpen} onClose={() => setEditEmailOpen(false)}>
        <DialogTitle>Change Email</DialogTitle>
        <DialogContent sx={{ minWidth: '400px' }}>
          <FormControl sx={sxPropsL}>
            <TextField
              size='small'
              required
              id='email'
              label='Email Address'
              value={newEmail}
              onChange={(event) => {
                setNewEmail(event.target.value);
              }}
            />
          </FormControl>
          <Typography>
            If the new email address exists as a different ClientId, this
            operation will reassign all enquiries and bookings to the new
            ClientId and the current ClientId 12557 will be deleted. To avoid
            deletion please tick the box below.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={preventReassign}
                onChange={() => setPreventReassign(!preventReassign)}
              />
            }
            label='Do NOT delete this client and reassign to existing. Throw error instead.'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditEmailOpen(false)}
            size='small'
            variant='outlined'
            color='primary'
          >
            Cancel
          </Button>
          <Button
            onClick={processEmailUpdate}
            size='small'
            variant='outlined'
            color='primary'
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditClient;
